import React, { useState } from "react";
import { CalendarPlus, ChevronDown, Plus, Save, Trash2 } from "lucide-react";
import { api } from "./api";
import { useConfirmation } from "./Confirmation";
import { BoutonSombre, Eyebrow, Section } from "./ui";

const jourFr = (iso) => (iso ? String(iso).split("-").reverse().join(".") : "");

/* Une période de portes ouvertes se situe toute seule dans le temps : passée, en cours ou à
   venir. Rien n'est effacé — l'école garde l'historique, et une période passée n'ouvre plus
   l'essai gratuit puisque la date du jour n'y tombe plus. */
function etatPeriode(p) {
  const jour = new Date().toISOString().slice(0, 10);
  if (!p.debut || !p.fin) return { label: "à compléter", style: { background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB" } };
  if (p.fin < jour) return { label: "passée", style: { background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB" } };
  if (p.debut > jour) return { label: "à venir", style: { background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" } };
  return { label: "en cours", style: { background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" } };
}

/* Calendrier réel des séances : c'est lui qui décide du prorata d'une inscription arrivée
   en cours de semestre (« 12 séances sur 18 »). */
export default function ReglagesCalendrier({ valeurs, onEnregistre }) {
  const { informer, demander } = useConfirmation();
  const depart = valeurs.calendrier || {};
  const officiel = valeurs.calendrier_officiel || {};
  const saisons = valeurs.calendrier_saisons || [];
  const [cal, setCal] = useState({
    saison: depart.saison || "",
    1: { debut: "", fin: "", ...(depart["1"] || {}) },
    2: { debut: "", fin: "", ...(depart["2"] || {}) },
    vacances: depart.vacances || [],
  });
  // Portes ouvertes : elles vivaient dans l'onglet Listes du classeur (colonnes AA/AB).
  // Elles décident du bandeau du site, de la page Portes ouvertes et de la gratuité de l'essai.
  const [portes, setPortes] = useState((valeurs.saison || {}).portes_ouvertes || []);
  const officielleIdentique = JSON.stringify({ un: officiel["1"], deux: officiel["2"], v: officiel.vacances })
    === JSON.stringify({ un: cal["1"], deux: cal["2"], v: cal.vacances });
  const [occupe, setOccupe] = useState(false);
  const [err, setErr] = useState("");
  const [voirPassees, setVoirPassees] = useState(false);
  const toutesSaisons = valeurs.calendrier_officiel_toutes || {};
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const estPassee = (v) => (v.fin || v.debut || "") !== "" && (v.fin || v.debut) < aujourdhui;
  const passees = cal.vacances.filter(estPassee);

  // Toutes les périodes officielles à venir qui ne sont pas déjà dans la liste : elles
  // viennent du calendrier du canton, chargé jusqu'en 2031.
  const deja = new Set(cal.vacances.map((v) => `${v.debut}|${v.fin}`));
  const suivantes = Object.values(toutesSaisons)
    .flatMap((c) => c.vacances || [])
    .filter((v) => v.debut >= aujourdhui && !deja.has(`${v.debut}|${v.fin}`))
    .sort((a, b) => a.debut.localeCompare(b.debut));
  const ajouterSuivantes = () => setCal({
    ...cal,
    vacances: [...cal.vacances, ...suivantes].sort((a, b) => String(a.debut).localeCompare(String(b.debut))),
  });

  const majSemestre = (s, champ, val) => setCal({ ...cal, [s]: { ...cal[s], [champ]: val } });
  const majVacances = (i, champ, val) => setCal({
    ...cal,
    vacances: cal.vacances.map((v, n) => (n === i ? { ...v, [champ]: val } : v)),
  });

  // Revenir au calendrier officiel du canton pour la saison en cours.
  const remettreOfficiel = async () => {
    if (!await demander({
      titre: "Reprendre les dates officielles",
      texte: "Les dates du canton remplacent tes modifications pour cette saison.",
      points: ["Rien n'est enregistré avant que tu cliques sur « Enregistrer ».",
        "Les factures déjà envoyées ne changent pas."],
      valider: "Reprendre les dates officielles",
    })) return;
    setCal({ saison: officiel.saison, 1: officiel["1"], 2: officiel["2"], vacances: officiel.vacances });
  };

  const enregistrer = async () => {
    setOccupe(true);
    setErr("");
    try {
      await api("/reglages", {
        method: "PATCH",
        body: JSON.stringify({
          valeurs: {
            calendrier: { ...cal, saison: officiel.saison || cal.saison },
            saison: {
              ...(valeurs.saison || {}),
              portes_ouvertes: portes.filter((p) => p.debut && p.fin && p.fin >= p.debut),
            },
          },
        }),
      });
      await onEnregistre();
      await informer({ titre: "Calendrier enregistré", ton: "succes",
        texte: "Le prorata des prochaines factures est calculé sur ces dates." });
    } catch (e) { setErr(e.message); }
    setOccupe(false);
  };

  return (
    <Section titre="Calendrier des séances" testid="reglages-calendrier"
      aide="Premier et dernier jour de cours de chaque semestre, puis les vacances et jours fériés. La console compte les séances de chaque cours sur cette base : c'est ce qui dit combien de séances comporte un semestre et quel semestre est en cours."
      action={
        <div className="flex items-center gap-2">
          {!officielleIdentique && (
            <button className="btn-ghost" onClick={remettreOfficiel} data-testid="calendrier-officiel">
              <CalendarPlus size={14} /> Reprendre les dates officielles
            </button>
          )}
          <BoutonSombre onClick={enregistrer} disabled={occupe} testid="calendrier-enregistrer">
            <Save size={14} /> Enregistrer
          </BoutonSombre>
        </div>
      }>
      <div className="rounded-2xl px-4 py-3 text-xs" data-testid="calendrier-mode-emploi"
        style={{ background: "var(--primary-tint)" }}>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="font-semibold">Ce que la console fait toute seule</div>
            <ul className="mt-1 space-y-0.5 text-[var(--muted)]">
              <li>· compter les séances de chaque cours (son jour de la semaine, congés déduits)</li>
              <li>· savoir combien de séances comporte chaque semestre (« 18 séances »)</li>
              <li>· savoir quel semestre est en cours et fermer les inscriptions au bon moment</li>
              <li>· archiver les stages dont la date est passée</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold">Tu n'as rien à saisir</div>
            <p className="mt-1 text-[var(--muted)]">
              Les vacances et jours fériés viennent du <strong>calendrier officiel du canton de Vaud</strong>,
              déjà chargé <strong>jusqu'en 2031</strong>. La saison suivante s'affiche toute seule dès que
              celle-ci est terminée, avec les deux règles de l'école appliquées :
              reprise <strong>une semaine après</strong> la rentrée officielle, arrêt <strong>une semaine
              avant</strong> les vacances d'été. Tu peux corriger une date si besoin : elle prend alors le
              dessus pour cette saison.
            </p>
          </div>
        </div>
        <p className="mt-2">
          Changer une date ne réécrit jamais l'historique : les factures déjà envoyées gardent leur
          montant et leur échéance.
        </p>
        <p className="mt-1 text-[var(--muted)]">
          Saison affichée : <strong>{cal.saison || officiel.saison || "—"}</strong>
          {officielleIdentique ? " · dates officielles du canton" : " · dates ajustées par l'école"}
          {saisons.length > 0 && ` · calendrier officiel chargé pour ${saisons.join(", ")}`}
        </p>
      </div>

      {err && <p className="text-sm" style={{ color: "#B91C1C" }}>{err}</p>}
      <div className="grid sm:grid-cols-2 gap-3">
        {["1", "2"].map((s) => (
          <div key={s} className="bg-white rounded-2xl border p-4" style={{ borderColor: "var(--border)" }}>
            <Eyebrow>Semestre {s}</Eyebrow>
            <div className="mt-2 space-y-2">
              <label className="block">
                <span className="text-xs text-[var(--muted)]">Premier jour de cours</span>
                <input type="date" className="input-field mt-1" value={cal[s].debut || ""}
                  data-testid={`calendrier-${s}-debut`}
                  onChange={(e) => majSemestre(s, "debut", e.target.value)} />
              </label>
              <label className="block">
                <span className="text-xs text-[var(--muted)]">Dernier jour de cours</span>
                <input type="date" className="input-field mt-1" value={cal[s].fin || ""}
                  data-testid={`calendrier-${s}-fin`}
                  onChange={(e) => majSemestre(s, "fin", e.target.value)} />
              </label>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border p-4" style={{ borderColor: "var(--border)" }}>
        <Eyebrow>Vacances et jours fériés ({cal.vacances.length - passees.length} à venir)</Eyebrow>
        <div className="mt-2 space-y-2">
          {cal.vacances.map((v, i) => [v, i]).filter(([v]) => voirPassees || !estPassee(v)).map(([v, i]) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`calendrier-vacances-${i}`}
              style={estPassee(v) ? { opacity: 0.5 } : undefined}>
              <input className="input-field" style={{ flex: 1, minWidth: 200 }} value={v.label || ""}
                placeholder="Vacances d'automne"
                onChange={(e) => majVacances(i, "label", e.target.value)} />
              <input type="date" className="input-field" style={{ width: 160 }} value={v.debut || ""}
                onChange={(e) => majVacances(i, "debut", e.target.value)} />
              <input type="date" className="input-field" style={{ width: 160 }} value={v.fin || ""}
                onChange={(e) => majVacances(i, "fin", e.target.value)} />
              <button className="btn-ghost" data-testid={`calendrier-supprimer-${i}`}
                onClick={() => setCal({ ...cal, vacances: cal.vacances.filter((_, n) => n !== i) })}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        {/* Les dates déjà passées ne servent plus à rien à l'écran : elles restent en base
            (elles ont compté les séances du semestre) mais sont masquées par défaut. */}
        {passees.length > 0 && (
          <button className="btn-ghost mt-3" data-testid="calendrier-voir-passees"
            onClick={() => setVoirPassees(!voirPassees)}>
            {voirPassees ? "Masquer" : "Voir"} les {passees.length} date(s) déjà passée(s)
          </button>
        )}
        <button className="btn-ghost mt-3" data-testid="calendrier-ajouter"
          onClick={() => setCal({ ...cal, vacances: [...cal.vacances, { label: "", debut: "", fin: "" }] })}>
          <Plus size={13} /> Ajouter une période
        </button>
        {/* Les saisons suivantes sont déjà connues (calendrier officiel du canton jusqu'en
            2031) : ce bouton les déplie ICI, dans le même format modifiable, pour que l'école
            ne les saisisse jamais une deuxième fois. */}
        {suivantes.length > 0 && (
          <button className="btn-ghost mt-3" data-testid="calendrier-deplier-suivantes"
            onClick={ajouterSuivantes}>
            <ChevronDown size={13} /> Déplier les {suivantes.length} date(s) des saisons suivantes (jusqu'en 2031)
          </button>
        )}
        <div className="mt-4 pt-3 border-t flex items-center gap-3" style={{ borderColor: "var(--border-soft)" }}>
          <BoutonSombre onClick={enregistrer} disabled={occupe} testid="vacances-enregistrer">
            <Save size={14} /> Enregistrer les vacances
          </BoutonSombre>
          <span className="text-[11px] text-[var(--muted)]">
            Enregistre aussi les dates des semestres et les portes ouvertes.
          </span>
        </div>
        <p className="text-[11px] text-[var(--muted)] mt-3">
          Saison {cal.saison || officiel.saison} : du {jourFr(cal["1"].debut)} au {jourFr(cal["2"].fin)} —
          la semaine de marge après la rentrée et avant les vacances d'été est déjà déduite.
        </p>
      </div>
      <div className="bg-white rounded-2xl border p-4" style={{ borderColor: "var(--border)" }}
        data-testid="calendrier-portes-ouvertes">
        <Eyebrow>Portes ouvertes ({portes.length})</Eyebrow>
        <p className="text-[11px] text-[var(--muted)] mt-1">
          Pendant ces périodes, le cours d'essai est <strong>gratuit</strong> et le site affiche son
          bandeau d'annonce. <strong>C'est ici que le site les lit</strong> : ce que tu enregistres
          ci-dessous s'applique au site dans la minute.
        </p>
        <div className="mt-3 space-y-2">
          {portes.map((p, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`portes-ouvertes-${i}`}>
              <input className="input-field" style={{ flex: 1, minWidth: 200 }} value={p.label || ""}
                placeholder="Portes ouvertes de rentrée"
                data-testid={`portes-ouvertes-${i}-label`}
                onChange={(e) => setPortes(portes.map((x, n) => (n === i ? { ...x, label: e.target.value } : x)))} />
              <input type="date" className="input-field" style={{ width: 160 }} value={p.debut || ""}
                data-testid={`portes-ouvertes-${i}-debut`}
                onChange={(e) => setPortes(portes.map((x, n) => (n === i ? { ...x, debut: e.target.value } : x)))} />
              <input type="date" className="input-field" style={{ width: 160 }} value={p.fin || ""}
                data-testid={`portes-ouvertes-${i}-fin`}
                onChange={(e) => setPortes(portes.map((x, n) => (n === i ? { ...x, fin: e.target.value } : x)))} />
              <button className="btn-ghost" data-testid={`portes-ouvertes-supprimer-${i}`}
                onClick={() => setPortes(portes.filter((_, n) => n !== i))}>
                <Trash2 size={13} />
              </button>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                data-testid={`portes-ouvertes-${i}-etat`}
                style={etatPeriode(p).style}>{etatPeriode(p).label}</span>
            </div>
          ))}
          {portes.length === 0 && (
            <p className="text-xs text-[var(--muted)]">
              Aucune période réglée : le site n'affiche aucune porte ouverte.
            </p>
          )}
        </div>
        <button className="btn-ghost mt-3" data-testid="portes-ouvertes-ajouter"
          onClick={() => setPortes([...portes, { label: "", debut: "", fin: "" }])}>
          <Plus size={13} /> Ajouter une période
        </button>
        <div className="mt-4 pt-3 border-t flex items-center gap-3" style={{ borderColor: "var(--border-soft)" }}>
          <BoutonSombre onClick={enregistrer} disabled={occupe} testid="portes-ouvertes-enregistrer">
            <Save size={14} /> Enregistrer les portes ouvertes
          </BoutonSombre>
          <span className="text-[11px] text-[var(--muted)]">
            Une période passée reste ici comme historique : elle est marquée « passée » et
            n'ouvre plus l'essai gratuit. Rien à effacer à la main.
          </span>
        </div>
      </div>
    </Section>
  );
}
